import { Component, Input, OnInit, ElementRef } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { AppSettings } from 'src/app/app.settings';
import { AppEnum } from 'src/app/appenum';
import { AppService } from 'src/app/services/app.service';
import { EncryptionService } from 'src/app/services/encryption.service';
import { IdbService } from 'src/app/services/idb.service';
import { environment } from 'src/environments/environment';
import { tempDynamicStoreconfig } from '../tempDynamicStoreconfig';
import { wrapAllMethodsWithLogging } from 'src/app/core/decorators/log-all-errors.decorator';
import { LoggingService } from 'src/app/services/logging.service';

@Component({
  selector: 'app-delete-media',
  templateUrl: './delete-media.component.html',
  styleUrls: ['./delete-media.component.scss'],
  standalone: false
})
export class DeleteMediaComponent implements OnInit {

  @Input() sectionName: string = '';
  @Input() mediaItems: any[] = [];
  @Input() templateId: any;

  readonly maxVisible = 3;
  deleteMediaMsg: any;
  imagesTodelete: any = [];
  taskId: any;
  orgId: any;

  // --- Slide to delete state ---
  thumbLeft = 4;
  fillWidth = 46;
  hintOpacity = 1;
  slideProgress = 0;
  thumbIcon = 'trash-outline';
  slideState: 'idle' | 'loading' | 'error' = 'idle';
  slideStateText = '';
  slideLocked = false;

  private readonly PAD = 4;
  private readonly THUMB_W = 46;
  private readonly THRESHOLD = 0.75;
  private dragging = false;
  private startX = 0;
  private trackWidth = 0;
  uncheckingTitle: any;
  uncheckingMessage: any;
  componentName: string = 'delete-media';

  get visibleThumbnails() {
    return this.mediaItems.slice(0, this.maxVisible);
  }

  get extraCount() {
    return Math.max(0, this.mediaItems.length - this.maxVisible);
  }

  getMediaPath(media: any): string {
    return media.localFileUrl || '';
  }

  getFileExtension(fileName: string): string {
    return fileName?.split('.').pop()?.toUpperCase() ?? 'FILE';
  }

  constructor(
    private _modalController: ModalController,
    private _alertController: AlertController,
    private _translateService: TranslateService,
    private encryptionService: EncryptionService,
    private _appenum: AppEnum,
    private _appServices: AppService,
    private appSetting: AppSettings,
    private idbservice: IdbService,
    private loggingService: LoggingService,
    private _tempDynamicStoreconfig: tempDynamicStoreconfig,
    private el: ElementRef
  ) {
    wrapAllMethodsWithLogging(this, this.componentName);
    this.deleteMediaMsg = this._translateService.instant('common_text.delete_confirmation_text_without_controlType');
    this.taskId = this.encryptionService.decrypt(this.encryptionService.localstorageDecrypt(this._appenum.EntityOfLocalStorageKeys.taskId));
    this.orgId = this.encryptionService.decrypt(this.encryptionService.localstorageDecrypt(this._appenum.EntityOfLocalStorageKeys.orgId));

  }

  ngOnInit(): void {
    this.uncheckingTitle = this._translateService.instant('self_pwa_text.unchecking_text', {
      controlName: this.sectionName
    });
    this.uncheckingMessage = this._translateService.instant('self_pwa_text.uncheck_capture_media_text', {
      controlName: this.sectionName
    });
  }

  // ── Slide handlers ──────────────────────────────────────

  onSlideStart(e: MouseEvent | TouchEvent) {
    if (this.slideLocked) return;
    this.dragging = true;
    const track = this.el.nativeElement.querySelector('.slide-track');
    this.trackWidth = track.offsetWidth;
    const clientX = e instanceof TouchEvent ? e.touches[0].clientX : e.clientX;
    this.startX = clientX - (this.thumbLeft - this.PAD);
  }

  onSlideMove(e: MouseEvent | TouchEvent) {
    if (!this.dragging || this.slideLocked) return;
    const clientX = e instanceof TouchEvent ? e.touches[0].clientX : e.clientX;
    const max = this.trackWidth - this.THUMB_W - this.PAD * 2;
    const pos = Math.min(Math.max(0, clientX - this.startX), max);
    this.thumbLeft = this.PAD + pos;
    this.fillWidth = this.THUMB_W + pos;
    this.slideProgress = pos / max;
    this.hintOpacity = 1 - this.slideProgress;
  }

  onSlideEnd() {
    if (!this.dragging || this.slideLocked) return;
    this.dragging = false;
    if (this.slideProgress >= this.THRESHOLD) {
      this.slideLocked = true;
      this.confirmDeleteMedia();
    } else {
      this.snapBack();
    }
  }

  private snapBack() {
    this.thumbLeft = this.PAD;
    this.fillWidth = this.THUMB_W;
    this.hintOpacity = 1;
    this.slideProgress = 0;
    this.thumbIcon = 'trash-outline';
    this.slideState = 'idle';
    this.slideStateText = '';
  }

  // ── Existing logic (unchanged) ───────────────────────────

  dismiss(confirmed: boolean) {
    this._modalController.dismiss({ confirmed });
  }

  async confirmDeleteMedia() {
    this.slideState = 'loading';
    this.slideStateText = 'Deleting media...';
    this.thumbIcon = 'hourglass-outline';
    this._appServices.showLoader();

    this.mediaItems.forEach(key => {
      this.imagesTodelete.push(key.FileName);
    });

    const postJson = {
      taskId: this.taskId,
      fileName: this.imagesTodelete
    };

    const logRequest: any = {
      apiEndUrl: environment.endpoint.deleteMediaDetails,
      reqBody: postJson,
    };

    this._appServices.postDataWithPromise(environment.endpoint.deleteMediaDetails, postJson).then(async (res) => {
      console.log("res", res)
      this._appServices.hideLoader();
      this.loggingService.handleResponseLog(
        res.StatusCode == 200
          ? 'info'
          : res.StatusCode == 402
            ? 'warning'
            : 'error',
        logRequest,
        res.StatusCode,
        res.Message,
        'deleteMediaDetails Api Call To Delete Media Using Slider',
        this.componentName,
        environment.endpoint.deleteMediaDetails
      );
      if (res.StatusCode == 200) {
        this.slideStateText = '';
        this.thumbIcon = 'checkmark-outline';
        this._appServices.presentToast(this._translateService.instant('common_text.file_delete_msg'));

        await this.idbservice.deleteMetaDataByFileNames(this.imagesTodelete, this.taskId, this.orgId, 'inActive');

        this._tempDynamicStoreconfig.fileMetaData = this._tempDynamicStoreconfig.fileMetaData.filter(
          (f: any) => !this.mediaItems.some((m: any) => m.controlId === f.controlId)
        );

        const templateData = this._tempDynamicStoreconfig.serverData.find(
          (t: any) => t.templateId === this.templateId
        );

        if (templateData) {
          this.mediaItems.forEach((m: any) => {
            const templateControl = templateData.templateJson.find(
              (c: any) => c.controlName === m.controlId
            );
            if (templateControl) delete templateControl.value;
          });

          this._tempDynamicStoreconfig.serverData = this._tempDynamicStoreconfig.serverData.map(
            (t: any) => t.templateId === this.templateId ? templateData : t
          );
        }
        this.imagesTodelete = [];
        await this._modalController.dismiss({ confirmed: true });

      } else if (res.StatusCode == 402) {
        await this.idbservice.deleteMetaDataByFileNames(this.imagesTodelete, this.taskId, this.orgId, 'inActive');
        this._tempDynamicStoreconfig.fileMetaData = this._tempDynamicStoreconfig.fileMetaData.filter(
          (f: any) => !this.mediaItems.some((m: any) => m.controlId === f.controlId)
        );

        const templateData = this._tempDynamicStoreconfig.serverData.find(
          (t: any) => t.templateId === this.templateId
        );

        if (templateData) {
          this.mediaItems.forEach((m: any) => {
            const templateControl = templateData.templateJson.find(
              (c: any) => c.controlName === m.controlId
            );
            if (templateControl) delete templateControl.value;
          });

          this._tempDynamicStoreconfig.serverData = this._tempDynamicStoreconfig.serverData.map(
            (t: any) => t.templateId === this.templateId ? templateData : t
          );
        }
        this.imagesTodelete = [];
        await this._modalController.dismiss({ confirmed: true });
      } else {
        this.slideState = 'error';
        this.slideStateText = 'Delete failed, try again';
        this.slideLocked = false;
        this._appServices.presentToast(
          res.Message[this.appSetting.selectedLanguage] != undefined
            ? res.Message[this._appServices.selectedLanguage]
            : res.Message[this.encryptionService.decrypt(this.encryptionService.localstorageDecrypt(this._appenum.EntityOfLocalStorageKeys.defaultLanguageSelected))]
        );
        setTimeout(() => this.snapBack(), 2000);
        await this._modalController.dismiss({ confirmed: false });
      }

    }, async (err) => {
      this._appServices.hideLoader();
      this.loggingService.logApiError(
        'error',
        logRequest,
        err,
        'deleteMediaDetails Api Call To Delete Media Using Slider',
        this.componentName,
        environment.endpoint.deleteMediaDetails
      )
      this.slideState = 'error';
      this.slideStateText = 'Delete failed, try again';
      this.slideLocked = false;
      setTimeout(() => this.snapBack(), 2000);
      await this._modalController.dismiss({ confirmed: false });
    });
  }
}